import * as React from 'react';
import cx from 'clsx';

import { QuicksilverTileOutfitType } from '../../game-tile';

import ClosedOverlay from '../ClosedOverlay';
import ModularGridGameTile from '../ModularGridGameTile';
import {
  GameTileProps,
  GameClickProps,
  OverlayProps,
  GridSizeType,
} from '../types';

import styles from './GameDual.module.scss';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { MEDIUM_BREAKPOINT, BIG_OUTFIT, MEDIUM_OUTFIT } from '../constants';
import { sliceGameList } from '../utils';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';

interface GameDualProps {
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game: GameClickProps;
    path: string;
  }) => void;
  insideHeader?: boolean;
  overlay?: OverlayProps;
  gameDetailsInfo: boolean;
  gameInfoTooltip: string;
  size?: GridSizeType;
  gamesLimit?: number;
  initialIndex?: number;
  categoryAttributes?: CategoryFinancialWebAttributes;
}

const GameDual: React.FC<GameDualProps> = props => {
  const {
    onClick,
    gameList,
    onInfoClick,
    insideHeader = true,
    overlay,
    gameDetailsInfo,
    gameInfoTooltip,
    size,
    gamesLimit = 5,
    initialIndex = 0,
    categoryAttributes,
  } = props;
  const windowWidth = useWindowSize().width;
  if (!gameList && !overlay) return null;
  const createSlot = ({
    position,
    outfit,
    slot,
    tileSize,
  }: {
    position: number;
    outfit: QuicksilverTileOutfitType;
    slot?: GameTileProps;
    tileSize?: 'small' | 'large';
  }) => (
    <div
      className={cx(styles.slot, {
        [styles.slotSmall]: size === undefined,
        [styles.slotSmallForced]: size === 'small',
      })}
      data-testid={`${position}-slot`}
      key={`${position}-key`}
    >
      <ModularGridGameTile
        data-testid="modular-grid-tile"
        game={slot}
        onClick={handleClick(position, onClick)}
        onInfoClick={handleClick(position, onInfoClick)}
        outfit={outfit}
        isLazy
        isAnchor
        tileSize={tileSize}
        ghost={!!overlay}
        gameDetailsInfo={gameDetailsInfo}
        gameInfoTooltip={gameInfoTooltip}
        categoryAttributes={categoryAttributes}
      />
    </div>
  );

  const handleClick = (gamePosition: number, fn: Function) => (
    game: GameClickProps,
    path?: string
  ) => {
    fn({ game, gamePosition, ...(path && { path }) });
  };

  const pickOutfit = (index: number) => {
    if (index === 0) return BIG_OUTFIT;
    if (windowWidth >= MEDIUM_BREAKPOINT.min && index === 3) return BIG_OUTFIT;
    if (windowWidth < MEDIUM_BREAKPOINT.min && index === 1) return BIG_OUTFIT;
    return MEDIUM_OUTFIT;
  };

  const preparedGameList = sliceGameList(gamesLimit, gameList);

  return (
    <div
      className={cx(styles.container, insideHeader && styles.insideHeader)}
      data-testid="game-dual-grid"
    >
      {(windowWidth < MEDIUM_BREAKPOINT.min && size === undefined) ||
      size === 'small' ? (
        <div className={styles.row}>
          {preparedGameList.map((g, i) =>
            createSlot({
              position: i + initialIndex + 1,
              outfit: pickOutfit(i),
              slot: g,
              tileSize: [0, 1].includes(i) ? 'large' : 'small',
            })
          )}
        </div>
      ) : (
        <div className={styles.rowWrapper}>
          <div className={cx(styles.row, styles.rowMedium)}>
            {createSlot({
              position: 1 + initialIndex,
              outfit: pickOutfit(0),
              slot: preparedGameList[0],
              tileSize: 'large',
            })}
          </div>
          <div className={cx(styles.row, styles.rowMedium)}>
            {createSlot({
              position: 2 + initialIndex,
              outfit: pickOutfit(1),
              slot: preparedGameList[1],
            })}
            {createSlot({
              position: 3 + initialIndex,
              outfit: pickOutfit(2),
              slot: preparedGameList[2],
            })}
          </div>
          <div className={cx(styles.row, styles.rowMedium)}>
            {createSlot({
              position: 4 + initialIndex,
              outfit: pickOutfit(3),
              slot: preparedGameList[3],
              tileSize: 'large',
            })}
          </div>
          <div className={cx(styles.row, styles.rowMedium)}>
            {createSlot({
              position: 5 + initialIndex,
              outfit: pickOutfit(4),
              slot: preparedGameList[4],
            })}
            {createSlot({
              position: 6 + initialIndex,
              outfit: pickOutfit(5),
              slot: preparedGameList[5],
            })}
          </div>
        </div>
      )}
      {!!overlay && <ClosedOverlay {...overlay} />}
    </div>
  );
};
export default GameDual;
